import React from 'react';
import { useNavigate } from 'react-router-dom';
import { object, string, number, boolean, mixed } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Form, {
  Arquivo,
  Botao,
  CaixaDeSelecao,
  CaixaDeTexto,
  Campo,
  Contexto,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import Usuario from 'Interface/Usuarios';
import {
  useAdicionarCursoMutation,
  useRecuperarCategoriasQuery,
} from 'Service';
import { CARGA_HORARIA, TECNOLOGIAS, TIPOS_CURSOS_IESDE } from 'View/constants';

const regras = object({
  nome: string().required('Obrigatório'),
  categoria_id: number().required('Obrigatório'),
  tecnologia: string().required('Obrigatório'),
  nome_lti: string().nullable().optional(),
  codigo: string().nullable().optional(),
  tipo_iesde: string().nullable().optional(),
  descricao: string().nullable().optional(),
  objetivo: string().nullable().optional(),
  autores: string().nullable().optional(),
  publico_alvo: string().nullable().optional(),
  carga_horaria: number().positive().required('Obrigatório'),
  visivel: boolean().nullable(),
  destaque: boolean().nullable(),
  prazo: number().positive().required('Obrigatório'),
  imagem: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return ['image/png', 'image/jpeg', 'image/webp'].includes(valor.type);
      }
      return true;
    })
    .optional(),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const [adicionarCurso] = useAdicionarCursoMutation();

  const { data } = useRecuperarCategoriasQuery({
    finder: 'listaJson',
    limit: 100,
    sort: 'nome',
  });

  const enviar = async (dados: Usuario): Promise<void> => {
    try {
      await adicionarCurso(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao('/admin/cursos');
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <Cabecalho
        titulo="Adicionando curso"
        descricao="Preencha o formulário e clique em enviar para cadastrar um novo curso"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row">
          <div className="col-md-9">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Campo name="nome" rotulo="Nome" />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="categoria_id"
                    rotulo="Categoria"
                    opcoes={data?.categorias}
                    prefixar={<Icone nome="view-list" />}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="tecnologia"
                    rotulo="Tecnologia"
                    opcoes={TECNOLOGIAS}
                    prefixar={<Icone nome="cpu" />}
                  />
                </div>
                <Contexto>
                  {({ values }) => (
                    <React.Fragment>
                      {values.tecnologia == 'lti' && (
                        <div className="col-md-12">
                          <Campo
                            name="nome_lti"
                            rotulo="Nome do recurso LTI"
                            ajuda="Deve ser exatamente o mesmo nome cadastro na plataforma LTI"
                          />
                        </div>
                      )}
                      {values.tecnologia == 'iesde' && (
                        <React.Fragment>
                          <div className="col-md-6">
                            <Campo
                              name="codigo"
                              rotulo="Código do curso"
                              ajuda="Código que idêntifica o curso na plataforma do provedor"
                            />
                          </div>
                          <div className="col-md-6">
                            <Selecao
                              name="tipo_iesde"
                              rotulo="Tipo do curso"
                              opcoes={TIPOS_CURSOS_IESDE}
                            />
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}
                </Contexto>
                <div className="col-md-12">
                  <CaixaDeTexto name="descricao" rotulo="Descrição" />
                </div>
                <div className="col-md-6">
                  <CaixaDeTexto name="objetivo" rotulo="Objetivo" />
                </div>
                <div className="col-md-6">
                  <CaixaDeTexto name="autores" rotulo="Autores" />
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto
                    name="publico_alvo"
                    rotulo="Público alvo"
                    semMargem
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="bg-white shadow-sm rounded p-4">
              <Arquivo name="imagem" />
              <Selecao
                name="carga_horaria"
                rotulo="Carga horária"
                opcoes={CARGA_HORARIA}
              />
              <Campo name="prazo" rotulo="Prazo mínimo" />
              <CaixaDeSelecao name="visivel" rotulo="Visível para estudo" />
              <CaixaDeSelecao name="destaque" rotulo="Aparece em destaque" />

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink tema="light" to="/admin/cursos" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default Adicionar;
